import {Box, Card, IconButton, Paper, Stack} from '@mui/material';
import DeleteFilledIcon from '@mui/icons-material/Delete';
import {FileCell} from 'features/files/FileCell';
import {ButtonWithConfirm} from 'components/ButtonWithConfirm/ButtonWithConfirm';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {FileEntity, FilesCategoryType} from 'pages/FilesPage';
import {MouseEventHandler, useCallback, useState} from 'react';
import {EditOutlined} from '@mui/icons-material';
import {FileUploadDialog} from '@ui/FileUploadArea/FileUploadDialog';
import {FilesService, updateFile} from '@services/Files/FilesService';
import {downloadFile} from 'utils/api/file';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';
import {FileType} from './types';
import {useTranslation} from 'react-i18next';
import {ReactComponent as AutoAwesomeGradientIcon} from '@assets/icons/AutoAwesomeGradientIcon.svg';

type FileCardProps = {
    file: FileEntity;
    onDelete?: (fileId: string) => Promise<void> | void;
    width?: string;
    isFirst?: boolean;
    isLast?: boolean;
    isOnlyOne?: boolean;
    refetch?: () => Promise<void>;
    needEdit?: boolean;
    filesType?: FileType;
    category?: FilesCategoryType | null;
    disableEdit?: boolean;
    showAddTagButton?: boolean;
    dataManager?: boolean;
};

export const downloadFieldName = 'Скачать файл';

export const FileCard: React.FC<FileCardProps> = ({
    file,
    onDelete,
    width = '100%',
    isFirst,
    isLast,
    isOnlyOne,
    refetch,
    needEdit = true,
    filesType,
    category,
    disableEdit,
    showAddTagButton = true,
    dataManager = false,
}) => {
    console.log('dataManager', dataManager);
    const service = FilesService.getInstance();
    const [isEditDialogOpen, setEditDialogOpen] = useState(false);
    const [fileToEdit, setFileToEdit] = useState<FileEntity | null>(null);
    const borderRadius = isOnlyOne ? '4px' : isFirst ? '4px 4px 0 0' : isLast ? '0 0 4px 4px' : '0';

    const handleDownloadFile: MouseEventHandler = useCallback(
        async (event) => {
            event.preventDefault();
            const link = file?._links?.[downloadFieldName].href.replace(/.*api/, '') || '';
            const data = await service.downloadFileFromTable(link);
            downloadFile(data.data, file.originalFilename);
        },
        [file, service],
    );

    const handleEditClick = useCallback(() => {
        setFileToEdit(file);
        setEditDialogOpen(true);
    }, [file]);

    const handleDelete = useCallback(() => {
        onDelete?.(file.id);
    }, [file.id, onDelete]);

    const {t} = useTranslation('common');

    return (
        <Card sx={{width, borderRadius, boxShadow: 'none'}}>
            <Paper
                elevation={0}
                sx={{
                    height: '100%',
                    padding: ' 0 16px',
                    borderRadius: 'inherit',
                }}
            >
                <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    width='100%'
                >
                    <Stack
                        direction='row'
                        alignItems='center'
                        gap={1}
                        width='100%'
                    >
                        <InsertDriveFileOutlinedIcon color='primary' />
                        <Box
                            component='div'
                            width='100%'
                        >
                            <FileCell record={file} />
                        </Box>
                    </Stack>
                    {dataManager && (
                        <IconButton
                            size='small'
                            onClick={() => {}}
                            sx={{p: 0}}
                        >
                            <AutoAwesomeGradientIcon
                                width={30}
                                height={30}
                            />
                        </IconButton>
                    )}
                    {file._links?.[downloadFieldName] && (
                        <IconButton
                            size='small'
                            onClick={handleDownloadFile}
                        >
                            <GetAppOutlinedIcon fontSize='small' />
                        </IconButton>
                    )}

                    {needEdit && (
                        <>
                            <IconButton
                                disabled={disableEdit}
                                size='small'
                                onClick={handleEditClick}
                            >
                                <EditOutlined fontSize='small' />
                            </IconButton>
                            <FileUploadDialog
                                open={isEditDialogOpen}
                                onClose={() => setEditDialogOpen(false)}
                                onSave={() => {}}
                                onUpdate={updateFile}
                                category={category}
                                allTags={[]}
                                isEditing={true}
                                fileToEdit={fileToEdit}
                                refetch={refetch}
                                filesType={filesType}
                                showAddTagButton={showAddTagButton}
                            />
                        </>
                    )}
                    {Boolean(onDelete) && (
                        <ButtonWithConfirm
                            disabled={disableEdit}
                            icon={
                                <DeleteFilledIcon
                                    color='action'
                                    sx={{opacity: '.56'}}
                                    fontSize='small'
                                />
                            }
                            onConfirm={handleDelete}
                            title={`${t('deleteFile')} ${file.originalFilename}`}
                        />
                    )}
                </Stack>
            </Paper>
        </Card>
    );
};
